<template>
  <form-layout
    class="work-order-form"
    :loading="isLoading"
    @save="saveWorkOrder"
  >
    <template #title>
      <h1 class="uppercase">Add Work Order</h1>
    </template>
    <template #body>
      <bdg-text-field
        ref="title"
        name="Work order title"
        v-model="formData.title"
        required
      />
      <bdg-radios-group
        ref="status"
        name="Status"
        :options="statusOptions"
        v-model="formData.status"
        required
      />
      <bdg-dropdown
        ref="user"
        name="Assigned to"
        :options="users"
        v-model="formData.user_id"
        searchable
        required
      />
      <bdg-number-field
        class="w-1/2"
        name="Estimated hours"
        v-model="formData.estimated_hours"
        force-int
      />
      <bdg-dropdown
        ref="contact"
        name="Primary customer"
        :options="customers"
        v-model="formData.contact_id"
        searchable
        required
      />
      <div class="scheduled-due flex mt-6">
        <bdg-date-field
          name="Scheduled"
          placeholder="mm/dd/yyyy"
          v-model="formData.scheduled_for"
        />
        <bdg-date-field
          ref="dueDate"
          name="Due date"
          placeholder="mm/dd/yyyy"
          v-model="formData.due_date"
          required
        />
      </div>
      <bdg-dropdown
        class="w-1/2"
        name="Priority"
        :options="priorities"
        v-model="formData.priority"
      />
      <bdg-text-area name="Description" v-model="formData.description" />
      <bdg-text-area name="Note" v-model="formData.note.text" />
      <bdg-files-list
        class="mt-4"
        section-name="Attached files (optional)"
        :items="formData.files"
        @delete="removeFile"
      />
      <bdg-file-uploader
        :class="{
          'mt-2': formData.files && formData.files.length
        }"
        @change="onFileChange"
        multiple
      />
    </template>
  </form-layout>
</template>

<script>
import FormLayout from "../../layouts/FormLayout";
import BdgTextField from "../../../forms/fields/BdgTextField";
import BdgDropdown from "../../../forms/fields/BdgDropdown";
import BdgDateField from "../../../forms/fields/BdgDateField";
import BdgTextArea from "../../../forms/fields/BdgTextArea";
import BdgFileUploader from "../../../elements/files-list/BdgFileUploader";
import BdgFilesList from "../../../elements/files-list/BdgFilesList";
import { UserTypes } from "../../../constants/user-enums";
import { validateRequiredRefs } from "../../../helpers";
import BdgRadiosGroup from "../../../forms/fields/BdgRadiosGroup";
import {
  CompletionStatuses,
  CompletionStatusLabels
} from "../../../constants/enums";
import BdgNumberField from "../../../forms/fields/BdgNumberField";

export default {
  name: "WorkOrderForm",
  components: {
    BdgNumberField,
    BdgRadiosGroup,
    BdgFilesList,
    BdgFileUploader,
    BdgTextArea,
    BdgDateField,
    BdgDropdown,
    BdgTextField,
    FormLayout
  },
  props: {
    id: Number,
    title: String,
    estimated_hours: Number,
    hours_recorded: Number,
    user_id: Number,
    status: String,
    contact_id: Number,
    scheduled_for: String,
    due_date: String,
    priority: String,
    description: String,
    note: {
      type: Object,
      default: () => {
        return { text: null };
      }
    },
    files: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      formData: {
        id: this.id,
        contract_id: this.$route.params.id,
        title: this.title,
        estimated_hours: this.estimated_hours,
        hours_recorded: this.hours_recorded,
        user_id: this.user_id,
        status: this.status || CompletionStatuses.NOT_STARTED,
        contact_id: this.contact_id,
        scheduled_for: this.scheduled_for,
        due_date: this.due_date,
        priority: this.priority,
        description: this.description,
        note: Object.assign({}, this.note),
        files: this.files
      },
      priorities: ["low", "medium", "high"],
      statusOptions: Object.keys(CompletionStatusLabels).map(key => {
        return {
          label: CompletionStatusLabels[key],
          value: key
        };
      }),
      requiredRefs: ["title", "user", "contact", "dueDate"]
    };
  },
  computed: {
    userType() {
      return this.$store.state.userType;
    },
    users() {
      const users = this.$store.state.Users.usersOfSpecifiedTypes;
      if (users) {
        return users.map(u => {
          return {
            label: u.name,
            value: u.id
          };
        });
      }
      return users;
    },
    customers() {
      const customers = this.$store.state.Contacts.customers;
      if (customers) {
        return customers.map(customer => {
          return {
            label: customer.name,
            value: customer.id
          };
        });
      }
      return customers;
    },
    isStoringWorkOrder() {
      return this.$store.state.Contracts.WorkOrders.isStoringWorkOrder;
    },
    isUpdatingWorkOrder() {
      return this.$store.state.Contracts.WorkOrders.isUpdatingWorkOrder;
    },
    isLoading() {
      return this.id ? this.isUpdatingWorkOrder : this.isStoringWorkOrder;
    }
  },
  created() {
    let userTypesQuery = [
      UserTypes.ADMIN,
      UserTypes.CREW_SUPERVISOR,
      UserTypes.CREW_LEADER,
      UserTypes.DESIGNER
    ];
    if (this.userType !== UserTypes.ADMIN) {
      userTypesQuery = userTypesQuery.filter(type => type !== UserTypes.ADMIN);
    }
    this.$store.dispatch("getUsers", userTypesQuery);
    this.$store.dispatch("getCustomers");
  },
  methods: {
    onFileChange(files) {
      if (files && files.length) {
        this.formData.files = Array.from(files);
      }
    },
    removeFile(file) {
      this.formData.files.splice(this.formData.files.indexOf(file), 1);
    },
    validate() {
      return validateRequiredRefs(this, true);
    },
    saveWorkOrder() {
      if (!this.validate()) {
        return;
      }

      if (this.id) {
        this.$store.dispatch("updateWorkOrder", this.formData);
      } else {
        this.$store.dispatch("storeWorkOrder", this.formData);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../scss/assets";

.work-order-form {
  .scheduled-due {
    & > .bdg-date-field {
      @include evenlySpacedFlexChild(2, 40px);
    }
  }
}
</style>
